import { Dispatch, SetStateAction, useState } from "react";

import { useTranslation } from "react-i18next";

import useTableItemDetails from "@/components/DetailsSection/hooks/useTableItemDetails";
import { ALERT_STATUS } from "@/context/alert/types";
import { refreshTableAndUpdateDetails } from "@/shared/functions/functions";
import { useIntegrationsDialogStore } from "@/stores/useIntegrationsDialogStore";
import { useIntegrationsTableStore } from "@/stores/useTableStore";

import { EditIcon, ReplayIcon } from "@dotnet/telematics-design-system";

import useAxiosPrivate from "../../../../../api/hooks/useAxiosPrivate";
import { ConvertedIntegrationsRow } from "../../../../../Pages/Integrations/types";
import {
  INTEGRATION_STATUS,
  INTEGRATIONS_ACTIONS,
  IntegrationsErrorDialogSteps,
  RenderConditionOptions,
  ROUTES
} from "../../../../../shared";
import useResponse from "../../../../../shared/hooks/useResponse";
import { SharedIntegrationsDataType } from "../../../../DetailsSection/types";

const useIntegrationsInlineActions = () => {
  const { getData, postData } = useAxiosPrivate();
  const { handleCommonError, handleResponse } = useResponse();
  const { getTableItemDetails } = useTableItemDetails();
  const { t } = useTranslation();

  const showError = useIntegrationsDialogStore(state => state.showError);

  const [rowData, setRowData] = useState<SharedIntegrationsDataType | null>(
    null
  );
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);

  const openEditDialog = (data: any) => {
    setRowData(data);
    setIsEditDialogOpen(true);
  };

  const handleEditIntegration = async (
    currentRowData: ConvertedIntegrationsRow,
    setIsButtonLoading: Dispatch<SetStateAction<boolean>>
  ) => {
    setIsButtonLoading(true);
    try {
      const { data } = await getData(
        `${ROUTES.Integrations}/${currentRowData.type}/${currentRowData.id}`
      );

      openEditDialog(data);
    } catch (error) {
      handleCommonError(error, t);
    }
    setIsButtonLoading(false);
  };

  const handleRetryIntegration = async (
    currentRowData: ConvertedIntegrationsRow,
    setIsButtonLoading?: Dispatch<SetStateAction<boolean>>
  ) => {
    setIsButtonLoading?.(true);
    try {
      const { id: rowId, type, status } = currentRowData;

      const params = useIntegrationsTableStore.getState().params;
      const { contains: searchParams, selectedTableItem } = params;

      const { data } = await postData(
        `${ROUTES.Integrations}/${type}/${rowId}/Retry`
      );

      const itemHasOpenedDetails = selectedTableItem === rowId;

      refreshTableAndUpdateDetails({
        valueInSearchField: searchParams,
        resourceId: data.id,
        itemHasOpenedDetails,
        getTableItemDetails,
        handleResponse
      });

      if (data.connectionErrorCode) {
        showError(
          data.connectionErrorCode,
          data,
          IntegrationsErrorDialogSteps.Retry
        );
      } else {
        const integrationNameObj = {
          integrationName: currentRowData.name
        };

        if (status === INTEGRATION_STATUS.Interrupted) {
          handleResponse(
            ALERT_STATUS.Info,
            t(
              "Alert##retry interrupted integration success",
              integrationNameObj
            )
          );
        } else {
          handleResponse(
            ALERT_STATUS.Success,
            t("Alert##retry integration success", integrationNameObj)
          );
        }
      }
    } catch (error) {
      handleCommonError(error, t);
    }
    setIsButtonLoading?.(false);
  };

  const inlineActions = [
    {
      title: INTEGRATIONS_ACTIONS.RetryIntegration,
      icon: <ReplayIcon />,
      hasLoader: true,
      clickAction: handleRetryIntegration,

      renderConditions: ({ data }: RenderConditionOptions) => {
        const rowStatus = data.status;

        const showButton =
          rowStatus === INTEGRATION_STATUS.Failed ||
          rowStatus === INTEGRATION_STATUS.Interrupted ||
          rowStatus === INTEGRATION_STATUS.InvalidConfiguration;

        return showButton;
      }
    },

    {
      title: INTEGRATIONS_ACTIONS.EditIntegration,
      icon: <EditIcon />,
      hasLoader: true,
      clickAction: handleEditIntegration,
      renderConditions: () => true
    }
  ];

  return {
    inlineActions,
    rowData,
    isEditDialogOpen,
    setRowData
  };
};

export default useIntegrationsInlineActions;
